import { VARIABLE_REGEX } from "./regex";

/**
 * tokenizes a string into plain text parts and variable parts.
 *
 * returns an array of:
 *   { type: 'text', text: string }
 *   { type: 'variable', name, raw, resolved, source, args }
 *
 * @param {string} text
 * @param {{ resolveVariable: (name: string, args: string[]) => { value, source } }} resolver
 */
export const tokenizeText = (text, resolver) => {
  if (!text || typeof text !== "string") return [{ type: "text", text: text || "" }];

  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(VARIABLE_REGEX.source, VARIABLE_REGEX.flags);
  let match;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, varName, argsStr] = match;
    const start = match.index;

    if (start > lastIndex) {
      parts.push({ type: "text", text: text.slice(lastIndex, start) });
    }

    const args = argsStr ? argsStr.split(/\s+/).filter(Boolean) : [];
    const { value, source } = resolver.resolveVariable(varName, args);

    parts.push({
      type: "variable",
      name: varName,
      raw: fullMatch,
      resolved: value !== undefined ? String(value) : fullMatch,
      source,
      args,
    });

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", text: text.slice(lastIndex) });
  }

  return parts.length ? parts : [{ type: "text", text }];
};

/**
 * replaces all {{...}} tokens in text with their resolved values.
 * unresolved variables are left as-is (e.g. {{missingVar}}).
 *
 * @param {string} text
 * @param {{ resolveVariable: Function }} resolver
 * @returns {string}
 */
export const interpolateText = (text, resolver) => {
  const parts = tokenizeText(text, resolver);
  return parts.map((part) => (part.type === "variable" ? part.resolved : part.text)).join("");
};
